import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Col, Container, Form, Modal as BootstrapModal, Row, Stack, Tab } from 'react-bootstrap'

import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../store/sync/sync'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAppSelector } from '../../../../store/hooks'

import { MissingScopeAction } from '../../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { FormFeedback } from '../../../../components/Form/FormFeedback'
import { Button } from '../../../../components/Button/Button'
import { Modal } from '../../../../components/Modal/Modal'
import { Tabs } from '../../../../components/Tabs/Tabs'

import { orionSettingsDefaultValues, OrionSettingsFormValues } from '../../../Integration/IntegrationManagement/IntegrationSpecificSettings/Orion/model'

export type OrionSettingsModalProps = {
  show: boolean
  onHide: () => void
}

const ORION_CONTACT_SYNC_MAX_OBJECT_COUNT = 5000

export const OrionSettingsModal: React.FC<OrionSettingsModalProps> = ({ show, onHide }) => {
  const [activeTabKey, setActiveTabKey] = useState<string>('orionSyncSettings')
  const changeTab = (key: string | null) => {
    if (key) {
      setActiveTabKey(key)
    }
  }

  const OrionSettingsModalBody = () => {
    return (
      <Tabs activeKey={activeTabKey} id='orion-settings' className='d-flex mb-3' onSelect={changeTab} mountOnEnter unmountOnExit>
        <Tab eventKey='orionSyncSettings' title='Contact Sync'>
          <OrionContactSyncSettings />
        </Tab>
      </Tabs>
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<BootstrapModal.Title>Edit Orion Integration Settings</BootstrapModal.Title>}
      body={<OrionSettingsModalBody />}
    />
  )
}

const OrionContactSyncSettings = () => {
  const accountId = useAppSelector(selectAccountId)

  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'orion',
    direction: 'inbound',
    object: 'contact_operation_hashed',
    scheduling: '*/30 * * * *',
    payload: {
      maxObjectCount: ORION_CONTACT_SYNC_MAX_OBJECT_COUNT,
    },
  } as CheckpointRequest

  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()

  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  const form = useForm<OrionSettingsFormValues>({ defaultValues: orionSettingsDefaultValues })

  useEffect(() => {
    if (isGetCheckpointFetching || !checkpoint) {
      return
    }
    form.setValue('inbound.contact.autoSync', checkpoint.data?.active || false)
  }, [isGetCheckpointFetching, checkpoint, form])

  const onSubmit = async (data: OrionSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (!checkpoint?.data) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest)
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCancel = () => {
    form.reset()
  }

  if (!isUpdating && isGetCheckpointFetching) {
    return (
      <>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isGetCheckpointFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isCreateCheckpointLoading || isUpdateCheckpointLoading}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isCreateCheckpointLoading || isUpdateCheckpointLoading}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
